<template>
  <div class="card">
    <div
      class="card-header"
      style="display: flex; justify-content: space-between; align-items: center"
    >
      <span class="font-weight-bold">{{ $t("emploiActivites.title") }}</span>
      <a-button
        type="primary"
        icon="plus"
        :disabled="!selectedActivityId"
        @click="openCreateModal"
      >
        {{ $t("emploiActivites.addSession") }}
      </a-button>
    </div>

    <div class="card-body">
      <div class="d-flex align-items-center mb-3 flex-wrap" style="gap: 8px">
        <label class="font-weight-bold mb-0 mr-2">{{ $t("accueil.activite") }}:</label>
        <a-select
          v-model="selectedActivityId"
          :placeholder="$t('inscription.chercherParActivite')"
          style="width: 280px"
          allow-clear
          show-search
          option-filter-prop="children"
          :filter-option="filterOption"
          @change="handleActivityChange"
        >
          <a-select-option
            v-for="activity in activities"
            :key="activity._id"
            :value="activity._id"
          >
            {{ activity.designation }}
          </a-select-option>
        </a-select>

        <template v-if="selectedActivityId">
          <label class="font-weight-bold mb-0 mr-2">{{ $t("sms.groupe") }}:</label>
          <a-select
            v-model="selectedGroupFilter"
            :placeholder="$t('emploiActivites.filterByGroup')"
            style="width: 200px"
            allow-clear
            @change="handleGroupFilter"
          >
            <a-select-option value="">
              {{ $t("recette.tous") }}
            </a-select-option>
            <a-select-option value="__all__">
              {{ $t("emploiActivites.wholeActivity") }}
            </a-select-option>
            <a-select-option
              v-for="group in activityGroups"
              :key="group.id"
              :value="group.id"
            >
              {{ group.name }}
            </a-select-option>
          </a-select>
          <a-button type="link" class="p-0" @click="clearFilters">
            {{ $t("action.reinitialiser") }}
          </a-button>
        </template>

        <span
          v-if="!selectedActivityId"
          class="text-muted ml-auto"
          style="font-size: 12px; max-width: 320px"
        >
          {{ $t("emploiActivites.selectActivityHint") }}
        </span>
      </div>

      <a-empty
        v-if="!selectedActivityId"
        :description="$t('emploiActivites.selectActivityHint')"
      />

      <a-table
        v-else
        :loading="loading"
        :data-source="filteredSessions"
        :columns="columns"
        :pagination="pagination"
        rowKey="id"
        @change="handleTableChange"
      >
        <template slot="day" slot-scope="text">
          {{ formatDay(text) }}
        </template>
        <template slot="timeRange" slot-scope="text, record">
          {{ record.time }}{{ record.ends ? ` – ${record.ends}` : "" }}
        </template>
        <template slot="group" slot-scope="text, record">
          <span v-if="record.activityGroupId">{{ record.groupName }}</span>
          <a-tag v-else color="blue">{{ $t("emploiActivites.wholeActivity") }}</a-tag>
        </template>
        <template slot="teacher" slot-scope="text, record">
          {{ record.teacherName || "—" }}
        </template>
        <template slot="room" slot-scope="text, record">
          {{ record.roomName || "—" }}
        </template>
        <template slot="status" slot-scope="text, record">
          <a-tag v-if="record.canceled" color="orange">
            {{ $t("emploiActivites.canceled") }}
          </a-tag>
          <a-tag v-else color="green">
            {{ $t("emploiActivites.active") }}
          </a-tag>
        </template>
        <template slot="operation" slot-scope="text, record">
          <div class="d-flex" style="gap: 8px; flex-wrap: wrap">
            <a-button
              type="primary"
              size="small"
              :disabled="record.canceled"
              @click="openEditModal(record)"
            >
              <a-icon type="edit" />{{ $t("action.modifier") }}
            </a-button>
            <a-popconfirm
              v-if="!record.canceled"
              :title="$t('emploiActivites.confirmCancelSession')"
              @confirm="() => cancelSession(record)"
            >
              <a-button size="small">
                <a-icon type="close-circle" />{{
                  $t("emploiActivites.cancelSession")
                }}
              </a-button>
            </a-popconfirm>
            <a-popconfirm
              v-else
              :title="$t('emploiActivites.confirmRestoreSession')"
              @confirm="() => restoreSession(record)"
            >
              <a-button size="small">
                <a-icon type="undo" />{{ $t("emploiActivites.restoreSession") }}
              </a-button>
            </a-popconfirm>
            <a-popconfirm
              :title="$t('emploiActivites.confirmDeleteSession')"
              @confirm="() => deleteSession(record)"
            >
              <a-button type="danger" size="small">
                <a-icon type="delete" />{{ $t("action.supprimer") }}
              </a-button>
            </a-popconfirm>
          </div>
        </template>
      </a-table>
    </div>

    <a-modal
      :title="
        editingSession
          ? $t('emploiActivites.editSession')
          : $t('emploiActivites.addSession')
      "
      :visible="modalVisible"
      :confirmLoading="modalSaving"
      :width="560"
      @ok="saveSession"
      @cancel="closeModal"
    >
      <a-form layout="vertical">
        <a-form-item :label="$t('sms.groupe')">
          <a-select
            v-model="form.activityGroupId"
            allow-clear
            :placeholder="$t('emploiActivites.wholeActivity')"
          >
            <a-select-option value="">
              {{ $t("emploiActivites.wholeActivity") }}
            </a-select-option>
            <a-select-option
              v-for="group in activityGroups"
              :key="group.id"
              :value="group.id"
            >
              {{ group.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('emploi.enseignant')" required>
          <a-select
            v-model="form.teacherId"
            show-search
            option-filter-prop="children"
            :filter-option="filterOption"
            :loading="teachersLoading"
            :placeholder="$t('emploiActivites.selectTeacher')"
          >
            <a-select-option
              v-for="teacher in teachers"
              :key="teacher._id"
              :value="teacher._id"
            >
              {{ teacherLabel(teacher) }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <div class="d-flex" style="gap: 12px">
          <a-form-item :label="$t('emploiActivites.time')" required style="flex: 1">
            <a-time-picker
              v-model="form.time"
              format="HH:mm"
              style="width: 100%"
              :placeholder="$t('emploiActivites.time')"
            />
          </a-form-item>
          <a-form-item
            :label="$t('emploiActivites.duration')"
            required
            style="flex: 1"
          >
            <a-input-number
              v-model="form.duration"
              :min="15"
              :step="15"
              style="width: 100%"
            />
          </a-form-item>
        </div>
        <a-form-item :label="$t('emploi.salle')">
          <a-select
            v-model="form.roomId"
            allow-clear
            show-search
            option-filter-prop="children"
            :filter-option="filterOption"
            :placeholder="$t('emploiActivites.selectRoom')"
          >
            <a-select-option
              v-for="room in rooms"
              :key="room._id"
              :value="room._id"
            >
              {{ room.name || room.designation || room._id }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item v-if="!editingSession" :label="$t('emploiActivites.repeat')">
          <a-switch v-model="form.repeat" />
          <span class="ml-2 text-muted" style="font-size: 12px">
            {{ $t("emploiActivites.repeatHint") }}
          </span>
        </a-form-item>

        <template v-if="!editingSession && form.repeat">
          <a-form-item :label="$t('emploiActivites.repeatFrequency')" required>
            <a-radio-group v-model="form.repeatFrequency">
              <a-radio-button value="daily">
                {{ $t("emploiActivites.repeatDaily") }}
              </a-radio-button>
              <a-radio-button value="weekly">
                {{ $t("emploiActivites.repeatWeekly") }}
              </a-radio-button>
              <a-radio-button value="monthly">
                {{ $t("emploiActivites.repeatMonthly") }}
              </a-radio-button>
            </a-radio-group>
          </a-form-item>
          <div class="d-flex" style="gap: 12px">
            <a-form-item
              :label="$t('emploiActivites.startDate')"
              required
              style="flex: 1"
            >
              <a-date-picker
                v-model="form.startDate"
                style="width: 100%"
                format="DD/MM/YYYY"
                :placeholder="$t('emploiActivites.startDate')"
              />
            </a-form-item>
            <a-form-item
              :label="$t('emploiActivites.endDate')"
              required
              style="flex: 1"
            >
              <a-date-picker
                v-model="form.endDate"
                style="width: 100%"
                format="DD/MM/YYYY"
                :placeholder="$t('emploiActivites.endDate')"
                :disabled-date="disableEndBeforeStart"
              />
            </a-form-item>
          </div>
          <a-alert
            v-if="previewOccurrenceCount > 0"
            type="info"
            show-icon
            class="mb-3"
            :message="
              $t('emploiActivites.sessionsWillBeCreated', {
                count: previewOccurrenceCount,
              })
            "
          />
        </template>

        <a-form-item
          v-else
          :label="$t('emploiActivites.day')"
          required
        >
          <a-date-picker
            v-model="form.day"
            style="width: 100%"
            format="DD/MM/YYYY"
            :placeholder="$t('emploiActivites.day')"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import moment from "moment";
import { showApiError } from "@/utilities/showApiError";

export default {
  name: "EmploiActivites",
  data() {
    return {
      loading: false,
      activities: [],
      teachers: [],
      teachersLoading: false,
      rooms: [],
      activityGroups: [],
      sessionsByActivityId: {},
      selectedActivityId: "",
      selectedGroupFilter: "",
      modalVisible: false,
      modalSaving: false,
      editingSession: null,
      form: {
        activityGroupId: "",
        teacherId: undefined,
        day: null,
        time: null,
        duration: 60,
        roomId: undefined,
        repeat: false,
        repeatFrequency: "weekly",
        startDate: null,
        endDate: null,
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) =>
          this.$t("all.pagination.showTotal", {
            start: range[0],
            end: range[1],
            total,
          }),
      },
    };
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t("emploiActivites.day"),
          dataIndex: "day",
          key: "day",
          scopedSlots: { customRender: "day" },
        },
        {
          title: this.$t("emploiActivites.time"),
          key: "timeRange",
          scopedSlots: { customRender: "timeRange" },
        },
        {
          title: this.$t("sms.groupe"),
          key: "group",
          scopedSlots: { customRender: "group" },
        },
        {
          title: this.$t("emploi.enseignant"),
          key: "teacher",
          scopedSlots: { customRender: "teacher" },
        },
        {
          title: this.$t("emploi.salle"),
          key: "room",
          scopedSlots: { customRender: "room" },
        },
        {
          title: this.$t("inscriptionActivites.globalStatus"),
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: this.$t("action.actions"),
          key: "operation",
          scopedSlots: { customRender: "operation" },
          width: 320,
        },
      ];
    },
    currentSessions() {
      if (!this.selectedActivityId) return [];
      return this.sessionsByActivityId[this.selectedActivityId] || [];
    },
    filteredSessions() {
      let list = this.currentSessions.filter((s) => s.status !== "inactive");
      if (this.selectedGroupFilter === "__all__") {
        list = list.filter((s) => !s.activityGroupId);
      } else if (this.selectedGroupFilter) {
        list = list.filter((s) => s.activityGroupId === this.selectedGroupFilter);
      }
      return list.slice().sort((a, b) => {
        const da = moment(a.day).valueOf() - moment(b.day).valueOf();
        if (da !== 0) return da;
        return String(a.time || "").localeCompare(String(b.time || ""));
      });
    },
    selectedActivityName() {
      const found = this.activities.find((a) => a._id === this.selectedActivityId);
      return found ? found.designation : "";
    },
    previewOccurrenceCount() {
      if (this.editingSession || !this.form.repeat) return 0;
      return this.buildOccurrenceDates().length;
    },
  },
  watch: {
    filteredSessions(list) {
      this.pagination.total = list.length;
    },
  },
  async created() {
    this.loading = true;
    try {
      await Promise.all([
        this.loadActivities(),
        this.loadTeachers(),
        this.loadRooms(),
      ]);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    moment,
    filterOption(input, option) {
      const text =
        (option.componentOptions.children[0] &&
          option.componentOptions.children[0].text) ||
        "";
      return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
    teacherLabel(teacher) {
      if (!teacher) return "";
      const emp = teacher.employee || {};
      const name = [emp.firstName || teacher.firstName, emp.lastName || teacher.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      return name || teacher.fullName || teacher.userName || String(teacher._id);
    },
    formatDay(day) {
      return day ? moment.utc(day).format("DD/MM/YYYY") : "—";
    },
    toDateOnly(value) {
      return value ? moment(value).format("YYYY-MM-DD") : null;
    },
    disableEndBeforeStart(current) {
      if (!this.form.startDate || !current) return false;
      return current.isBefore(moment(this.form.startDate).startOf("day"));
    },
    async loadActivities() {
      try {
        const res = await apiClient.post("/activities/filter", {
          query: { status: "active" },
          aggregation: { $project: { _id: 1, designation: 1 } },
        });
        this.activities = res.data || [];
      } catch (err) {
        showApiError(this, err);
      }
    },
    async loadTeachers() {
      this.teachersLoading = true;
      try {
        const res = await apiClient.post("/teachers/filter", {
          query: {
            role: "teacher",
            status: "active",
          },
        });
        this.teachers = (res.data || []).slice().sort((a, b) =>
          this.teacherLabel(a).localeCompare(this.teacherLabel(b))
        );
      } catch (err) {
        showApiError(this, err);
        this.teachers = [];
      } finally {
        this.teachersLoading = false;
      }
    },
    async loadRooms() {
      try {
        const res = await apiClient.post("/rooms/filter", { query: {} });
        this.rooms = res.data || [];
      } catch (err) {
        showApiError(this, err);
        this.rooms = [];
      }
    },
    async loadActivityGroups(activityId) {
      if (!activityId) {
        this.activityGroups = [];
        return;
      }
      try {
        const res = await apiClient.get(`/activityGroup/byActivity/${activityId}`);
        this.activityGroups = (res.data.groups || []).map((g) => ({
          id: String(g._id || g.id),
          name: g.name,
        }));
      } catch (err) {
        showApiError(this, err);
        this.activityGroups = [];
      }
    },
    async handleActivityChange(value) {
      this.selectedActivityId = value || "";
      this.selectedGroupFilter = "";
      this.pagination.current = 1;
      if (!this.selectedActivityId) {
        this.activityGroups = [];
        return;
      }
      this.loading = true;
      try {
        await Promise.all([
          this.loadActivityGroups(this.selectedActivityId),
          this.loadSessions(this.selectedActivityId),
        ]);
      } finally {
        this.loading = false;
      }
    },
    handleGroupFilter(value) {
      this.selectedGroupFilter = value || "";
      this.pagination.current = 1;
    },
    clearFilters() {
      this.selectedActivityId = "";
      this.selectedGroupFilter = "";
      this.activityGroups = [];
      this.pagination.current = 1;
    },
    async loadSessions(activityId) {
      if (!activityId) return;
      try {
        const res = await apiClient.get(
          `/activitySchedules/byActivity/${activityId}`
        );
        this.$set(
          this.sessionsByActivityId,
          activityId,
          res.data.sessions || []
        );
      } catch (err) {
        showApiError(this, err);
        this.$set(this.sessionsByActivityId, activityId, []);
      }
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
    },
    resetForm() {
      this.form = {
        activityGroupId: "",
        teacherId: undefined,
        day: null,
        time: null,
        duration: 60,
        roomId: undefined,
        repeat: false,
        repeatFrequency: "weekly",
        startDate: null,
        endDate: null,
      };
    },
    openCreateModal() {
      if (!this.selectedActivityId) {
        this.$message.warning(this.$t("emploiActivites.selectActivityHint"));
        return;
      }
      this.editingSession = null;
      this.resetForm();
      this.modalVisible = true;
    },
    openEditModal(record) {
      this.editingSession = record;
      this.form = {
        activityGroupId: record.activityGroupId || "",
        teacherId: record.teacherId,
        day: record.day ? moment.utc(record.day) : null,
        time: record.time ? moment(record.time, "HH:mm") : null,
        duration: record.duration || 60,
        roomId: record.roomId || undefined,
        repeat: false,
        repeatFrequency: "weekly",
        startDate: null,
        endDate: null,
      };
      this.modalVisible = true;
    },
    closeModal() {
      this.modalVisible = false;
      this.editingSession = null;
      this.resetForm();
    },
    validateForm() {
      if (!this.form.teacherId) {
        this.$message.warning(this.$t("emploiActivites.selectTeacher"));
        return false;
      }
      if (!this.form.time) {
        this.$message.warning(this.$t("emploiActivites.timeRequired"));
        return false;
      }
      if (!this.form.duration || this.form.duration < 1) {
        this.$message.warning(this.$t("emploiActivites.durationRequired"));
        return false;
      }
      if (!this.editingSession && this.form.repeat) {
        if (!this.form.startDate || !this.form.endDate) {
          this.$message.warning(this.$t("emploiActivites.dateRangeRequired"));
          return false;
        }
        if (moment(this.form.endDate).isBefore(moment(this.form.startDate), "day")) {
          this.$message.warning(this.$t("emploiActivites.endBeforeStart"));
          return false;
        }
        if (!this.form.repeatFrequency) {
          this.$message.warning(this.$t("emploiActivites.frequencyRequired"));
          return false;
        }
        if (this.buildOccurrenceDates().length === 0) {
          this.$message.warning(this.$t("emploiActivites.noOccurrences"));
          return false;
        }
      } else if (!this.form.day) {
        this.$message.warning(this.$t("emploiActivites.dayRequired"));
        return false;
      }
      return true;
    },
    buildOccurrenceDates() {
      if (!this.form.repeat || !this.form.startDate || !this.form.endDate) {
        return [];
      }
      const start = moment(this.form.startDate).startOf("day");
      const end = moment(this.form.endDate).startOf("day");
      if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) {
        return [];
      }
      const dates = [];
      const freq = this.form.repeatFrequency || "weekly";
      if (freq === "daily") {
        const cursor = start.clone();
        while (cursor.isSameOrBefore(end, "day")) {
          dates.push(cursor.clone());
          cursor.add(1, "day");
        }
      } else if (freq === "weekly") {
        const cursor = start.clone();
        while (cursor.isSameOrBefore(end, "day")) {
          dates.push(cursor.clone());
          cursor.add(1, "week");
        }
      } else if (freq === "monthly") {
        const dayOfMonth = start.date();
        let cursor = start.clone();
        while (cursor.isSameOrBefore(end, "day")) {
          dates.push(cursor.clone());
          const next = cursor.clone().add(1, "month");
          const dim = next.daysInMonth();
          next.date(Math.min(dayOfMonth, dim));
          cursor = next.startOf("day");
        }
      }
      return dates;
    },
    buildSessionPayload(dayMoment) {
      const timeStr = moment(this.form.time).format("HH:mm");
      return {
        activityId: this.selectedActivityId,
        activityGroupId: this.form.activityGroupId || null,
        teacherId: this.form.teacherId,
        day: this.toDateOnly(dayMoment),
        time: timeStr,
        duration: Number(this.form.duration),
        roomId: this.form.roomId || null,
      };
    },
    async saveSession() {
      if (!this.validateForm()) return;
      this.modalSaving = true;
      try {
        if (this.editingSession) {
          const payload = this.buildSessionPayload(this.form.day);
          delete payload.activityId;
          await apiClient.patch(
            `/activitySchedules/${this.editingSession.id}`,
            payload
          );
          this.$message.success(this.$t("emploiActivites.sessionUpdated"));
        } else {
          const payload = this.buildSessionPayload(
            this.form.repeat ? this.form.startDate : this.form.day
          );
          if (this.form.repeat) {
            delete payload.day;
            payload.recurrence = {
              frequency: this.form.repeatFrequency,
              start: this.toDateOnly(this.form.startDate),
              end: this.toDateOnly(this.form.endDate),
            };
          }
          const res = await apiClient.post("/activitySchedules", payload);
          const count = res.data.count || 1;
          this.$message.success(
            this.form.repeat
              ? this.$t("emploiActivites.sessionsCreated", { count })
              : this.$t("emploiActivites.sessionCreated")
          );
        }
        await this.loadSessions(this.selectedActivityId);
        this.closeModal();
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.modalSaving = false;
      }
    },
    async cancelSession(record) {
      try {
        await apiClient.patch(`/activitySchedules/${record.id}/cancel`, {
          cancellationReason: "manual",
        });
        await this.loadSessions(this.selectedActivityId);
        this.$message.success(this.$t("emploiActivites.sessionCanceled"));
      } catch (err) {
        showApiError(this, err);
      }
    },
    async restoreSession(record) {
      try {
        await apiClient.patch(`/activitySchedules/${record.id}/restore`);
        await this.loadSessions(this.selectedActivityId);
        this.$message.success(this.$t("emploiActivites.sessionRestored"));
      } catch (err) {
        showApiError(this, err);
      }
    },
    async deleteSession(record) {
      try {
        await apiClient.delete(`/activitySchedules/${record.id}`);
        await this.loadSessions(this.selectedActivityId);
        this.$message.success(this.$t("emploiActivites.sessionDeleted"));
      } catch (err) {
        showApiError(this, err);
      }
    },
  },
};
</script>
